import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Edit2, Trash2, AlertTriangle, Package, X, Check, RefreshCw } from 'lucide-react';
import TopBar from '../components/layout/TopBar';
import {
  getInventory, createInventoryItem, updateInventoryItem,
  deleteInventoryItem, adjustStock
} from '../api';
import './InventoryPage.css';

/* Backend uses: quantity, minQuantity, unitPrice (BigDecimal → number in JSON)
   We display as: qty / minQty internally for brevity */

const CATEGORIES = ['All', 'Filters', 'Oils', 'Brakes', 'AC', 'Engine', 'Exterior', 'Steering'];
const BLANK = { name: '', sku: '', category: 'Filters', quantity: 0, minQuantity: 5, unitPrice: '', supplier: '' };

/* Map API response → display object (keeps backend field names, just adds isLow helper) */
const toRow = p => ({
  ...p,
  qty:    p.quantity    ?? 0,
  minQty: p.minQuantity ?? 0,
  price:  Number(p.unitPrice ?? 0),
});

export default function InventoryPage() {
  const [parts,      setParts]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [category,   setCategory]   = useState('All');
  const [showModal,  setShowModal]  = useState(false);
  const [editItem,   setEditItem]   = useState(null);
  const [form,       setForm]       = useState(BLANK);
  const [saving,     setSaving]     = useState(false);

  /* ── Load from backend ── */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getInventory();
      setParts((data || []).map(toRow));
    } catch (e) {
      console.error('Failed to load inventory:', e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /* ── Derived ── */
  const lowStock = parts.filter(p => p.qty <= p.minQty);
  const totalValue = parts.reduce((s, p) => s + p.qty * p.price, 0);

  const filtered = parts.filter(p => {
    const matchSearch = [p.name, p.sku, p.supplier].join(' ').toLowerCase().includes(search.toLowerCase());
    const matchCat    = category === 'All' || p.category === category;
    return matchSearch && matchCat;
  });

  /* ── Modal helpers ── */
  const openAdd  = () => { setEditItem(null); setForm(BLANK); setShowModal(true); };
  const openEdit = p  => {
    setEditItem(p);
    setForm({
      name:        p.name,
      sku:         p.sku,
      category:    p.category,
      quantity:    p.qty,
      minQuantity: p.minQty,
      unitPrice:   p.price,
      supplier:    p.supplier || '',
    });
    setShowModal(true);
  };

  /* ── Save (create or update) ── */
  const handleSave = async () => {
    if (!form.name.trim() || !form.sku.trim() || !form.unitPrice) return;
    setSaving(true);
    const payload = {
      name:        form.name.trim(),
      sku:         form.sku.trim().toUpperCase(),
      category:    form.category,
      quantity:    Number(form.quantity),
      minQuantity: Number(form.minQuantity),
      unitPrice:   Number(form.unitPrice),
      supplier:    form.supplier || '',
    };
    try {
      if (editItem) {
        const updated = await updateInventoryItem(editItem.id, payload);
        setParts(prev => prev.map(p => p.id === editItem.id ? toRow(updated) : p));
      } else {
        const created = await createInventoryItem(payload);
        setParts(prev => [...prev, toRow(created)]);
      }
      setShowModal(false);
    } catch (e) {
      alert('Save failed: ' + (e.response?.data?.message || e.message));
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete ── */
  const handleDelete = async id => {
    if (!window.confirm('Delete this part? This cannot be undone.')) return;
    try {
      await deleteInventoryItem(id);
      setParts(prev => prev.filter(p => p.id !== id));
    } catch (e) {
      alert('Delete failed: ' + (e.response?.data?.message || e.message));
    }
  };

  /* ── Adjust stock ± ── */
  const handleAdjustStock = async (id, delta) => {
    try {
      const updated = await adjustStock(id, delta);
      setParts(prev => prev.map(p => p.id === id ? toRow(updated) : p));
    } catch (e) {
      alert('Stock update failed: ' + (e.response?.data?.message || e.message));
    }
  };

  return (
    <>
      <TopBar title="Spare Parts Inventory" subtitle="Stock management and reorder tracking" />
      <div className="admin-content">

        <div className="page-hdr">
          <div className="page-hdr__left">
            <h1>Inventory</h1>
            <p>
              {loading ? 'Loading…' : `${parts.length} parts · ${lowStock.length} low stock · Total value ₹${totalValue.toLocaleString('en-IN')}`}
            </p>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button className="btn btn-ghost btn-sm" onClick={load} disabled={loading}>
              <RefreshCw size={14} className={loading ? 'spinning' : ''}/> Refresh
            </button>
            <button className="btn btn-primary" onClick={openAdd}><Plus size={15}/> Add Part</button>
          </div>
        </div>

        {/* Low stock alert */}
        {lowStock.length > 0 && (
          <div className="inv-alert">
            <AlertTriangle size={15}/>
            <span>
              <strong>{lowStock.length} items</strong> at or below minimum stock:{' '}
              {lowStock.map(p => p.name).join(', ')}
            </span>
          </div>
        )}

        {/* Toolbar */}
        <div className="inv-toolbar">
          <div style={{ position:'relative', flex:1, minWidth:240 }}>
            <Search size={14} style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'var(--text-muted)', pointerEvents:'none' }}/>
            <input className="input" style={{ paddingLeft:36 }}
              placeholder="Search parts, SKU, supplier…"
              value={search} onChange={e => setSearch(e.target.value)}/>
          </div>
          <div className="inv-category-tabs">
            {CATEGORIES.map(c => (
              <button key={c} className={`filter-chip ${category===c?'active':''}`} onClick={() => setCategory(c)}>{c}</button>
            ))}
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div style={{ textAlign:'center', padding:'60px 0', color:'var(--text-muted)' }}>
            <div className="appt-spinner"/>
            <p style={{ marginTop:12, fontSize:13 }}>Loading inventory…</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Part Name</th>
                  <th>SKU</th>
                  <th>Category</th>
                  <th>Stock</th>
                  <th>Min. Stock</th>
                  <th>Unit Price</th>
                  <th>Stock Value</th>
                  <th>Supplier</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={10} style={{ textAlign:'center', padding:'40px 0', color:'var(--text-muted)' }}>
                    {parts.length === 0 ? 'No parts in inventory yet. Click "Add Part" to get started.' : 'No parts match your search.'}
                  </td></tr>
                ) : filtered.map(p => {
                  const isLow = p.qty <= p.minQty;
                  const isOut = p.qty === 0;
                  return (
                    <tr key={p.id}>
                      <td>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <div style={{ width:30, height:30, borderRadius:6, background:'var(--accent-glow)', border:'1px solid var(--border-accent)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                            <Package size={13} style={{ color:'var(--accent)' }}/>
                          </div>
                          <span style={{ fontWeight:500, fontSize:13 }}>{p.name}</span>
                        </div>
                      </td>
                      <td><span style={{ fontFamily:'monospace', fontSize:12, color:'var(--text-muted)' }}>{p.sku}</span></td>
                      <td><span className="badge badge-neutral">{p.category}</span></td>
                      <td>
                        <span style={{ fontWeight:700, color: isOut?'var(--danger)':isLow?'var(--warning)':'var(--text-primary)', fontSize:14 }}>
                          {p.qty}
                        </span>
                      </td>
                      <td style={{ color:'var(--text-muted)', fontSize:13 }}>{p.minQty}</td>
                      <td style={{ fontWeight:500 }}>₹{p.price.toLocaleString('en-IN')}</td>
                      <td style={{ color:'var(--accent)', fontWeight:600 }}>₹{(p.qty * p.price).toLocaleString('en-IN')}</td>
                      <td style={{ fontSize:12, color:'var(--text-muted)' }}>{p.supplier}</td>
                      <td>
                        {isOut  ? <span className="badge badge-danger"><AlertTriangle size={9}/> Out</span>
                        : isLow ? <span className="badge badge-warning"><AlertTriangle size={9}/> Low</span>
                        : <span className="badge badge-success">OK</span>}
                      </td>
                      <td>
                        <div style={{ display:'flex', gap:4, alignItems:'center' }}>
                          <button className="tbl-btn" style={{ width:24, height:24, fontSize:14, fontWeight:700 }}
                            onClick={() => handleAdjustStock(p.id, -1)} disabled={p.qty === 0} title="Remove 1">−</button>
                          <button className="tbl-btn" style={{ width:24, height:24, fontSize:14, fontWeight:700 }}
                            onClick={() => handleAdjustStock(p.id, 1)} title="Add 1">+</button>
                          <button className="tbl-btn" onClick={() => openEdit(p)} title="Edit"><Edit2 size={13}/></button>
                          <button className="tbl-btn tbl-btn--danger" onClick={() => handleDelete(p.id)} title="Delete"><Trash2 size={13}/></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal__header">
                <h3 className="modal__title">{editItem ? 'Edit Part' : 'Add Spare Part'}</h3>
                <button className="modal__close" onClick={() => setShowModal(false)}><X size={18}/></button>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                <div style={{ gridColumn:'1/-1' }}>
                  <label className="label">Part Name *</label>
                  <input className="input" placeholder="e.g. Engine Oil Filter (Universal)"
                    value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}/>
                </div>
                <div>
                  <label className="label">SKU Code *</label>
                  <input className="input" placeholder="FLT-EOF-001"
                    value={form.sku} onChange={e => setForm(f => ({ ...f, sku: e.target.value }))}/>
                </div>
                <div>
                  <label className="label">Supplier</label>
                  <input className="input" placeholder="e.g. Bosch India"
                    value={form.supplier} onChange={e => setForm(f => ({ ...f, supplier: e.target.value }))}/>
                </div>
                <div>
                  <label className="label">Unit Price ₹ *</label>
                  <input className="input" type="number" min="0" placeholder="450"
                    value={form.unitPrice} onChange={e => setForm(f => ({ ...f, unitPrice: e.target.value }))}/>
                </div>
                <div>
                  <label className="label">Category</label>
                  <select className="input" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                    {CATEGORIES.filter(c => c !== 'All').map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Stock Qty</label>
                  <input className="input" type="number" min="0"
                    value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))}/>
                </div>
                <div>
                  <label className="label">Min. Qty (alert threshold)</label>
                  <input className="input" type="number" min="0"
                    value={form.minQuantity} onChange={e => setForm(f => ({ ...f, minQuantity: e.target.value }))}/>
                </div>
              </div>
              <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:24 }}>
                <button className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
                <button className="btn btn-primary" disabled={saving} onClick={handleSave}>
                  <Check size={14}/> {saving ? 'Saving…' : editItem ? 'Save Changes' : 'Add Part'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}